import { CookieService } from './CookieService';

export interface IUserData {
  displayName: string;
  emailAddress?: string;
  givenName: string;
  initials: string;
  userName?: string;
}

interface AuthData {
  user: IUserData;
  accessToken?: string;
}

class UserService {
  private static readonly SkipAutoSsoOnceKey = 'skipAutoSsoOnce';
  private static readonly LastLoginHintKey = 'lastLoginHint';

  private static CanUseStorage = (): boolean => typeof window !== 'undefined';

  public static GetUser = (): IUserData | null => {
    const data = CookieService.GetCookie('authUser');
    if (data) {
      try {
        const user = JSON.parse(data);
        return user;
      } catch (error) {
        UserService.ClearSession();
      }
    }
    return null;
  };

  public static GetToken = (): string | null => {
    const data = CookieService.GetCookie('authToken');
    if (data) {
      return data;
    }
    return null;
  };

  public static GetLoginHint = (): string | null => {
    const user = UserService.GetUser();

    if (user?.emailAddress) {
      return user.emailAddress;
    }

    if (user?.userName) {
      return user.userName;
    }

    if (!UserService.CanUseStorage()) {
      return null;
    }

    return (
      window.localStorage.getItem('userName') ||
      window.localStorage.getItem(UserService.LastLoginHintKey)
    );
  };

  public static IsLoggedIn = (): boolean => {
    if (!UserService.CanUseStorage()) {
      return false;
    }

    const isLoggedInFlag = window.localStorage.getItem('isLoggedIn') === 'true';
    const hasUser = UserService.GetUser() !== null;

    if (!isLoggedInFlag || !hasUser) return false;

    // Validate the stored access token has not expired.
    // If it has, clear the session so the login flow can obtain a fresh token.
    const token = UserService.GetToken();
    if (token) {
      try {
        const parts = token.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          if (payload.exp && payload.exp * 1000 < Date.now()) {
            UserService.ClearSession();
            return false;
          }
        }
      } catch {
        return false;
      }
    }

    return true;
  };

  public static SetUser = (data: AuthData) => {
    // Use secure cookie options when running over HTTPS (all non-localhost environments)
    const isSecure =
      typeof window !== 'undefined' && window.location.protocol === 'https:';
    const cookieOptions = isSecure
      ? { secure: true, sameSite: 'Strict' as const }
      : undefined;

    CookieService.SaveCookie(
      'authUser',
      JSON.stringify(data.user),
      cookieOptions
    );
    if (data.accessToken) {
      CookieService.SaveCookie('authToken', data.accessToken, cookieOptions);
    }

    if (UserService.CanUseStorage()) {
      window.localStorage.setItem('isLoggedIn', 'true');

      if (data.user.emailAddress || data.user.userName) {
        const loginHint = data.user.emailAddress || data.user.userName || '';
        window.localStorage.setItem('userName', loginHint);
        window.localStorage.setItem(UserService.LastLoginHintKey, loginHint);
      }
    }
  };

  public static ClearSession = (preserveLoginHint: boolean = false) => {
    const retainedLoginHint =
      preserveLoginHint && UserService.CanUseStorage()
        ? window.localStorage.getItem('userName') ||
          window.localStorage.getItem(UserService.LastLoginHintKey)
        : null;

    CookieService.DeleteCookie('authUser');
    CookieService.DeleteCookie('authToken');

    if (UserService.CanUseStorage()) {
      window.localStorage.removeItem('isLoggedIn');
      window.localStorage.removeItem('userName');
      window.localStorage.removeItem(UserService.LastLoginHintKey);

      if (retainedLoginHint) {
        window.localStorage.setItem('userName', retainedLoginHint);
        window.localStorage.setItem(
          UserService.LastLoginHintKey,
          retainedLoginHint
        );
      }
    }
  };

  public static MarkSkipAutoSsoOnce = () => {
    if (!UserService.CanUseStorage()) {
      return;
    }

    window.localStorage.setItem(UserService.SkipAutoSsoOnceKey, 'true');
  };

  public static ConsumeSkipAutoSsoOnce = (): boolean => {
    if (!UserService.CanUseStorage()) {
      return false;
    }

    const shouldSkip =
      window.localStorage.getItem(UserService.SkipAutoSsoOnceKey) === 'true';
    if (shouldSkip) {
      window.localStorage.removeItem(UserService.SkipAutoSsoOnceKey);
    }

    return shouldSkip;
  };
}

export { UserService };
